import { useAuth } from '../contexts/AuthContext';
import type { UserRole } from '../contexts/AuthContext';

export type { UserRole };

/** Permissões padrão por papel administrativo */
const roleDefaultPermissions: Record<string, UserRole[]> = {
  dashboard:        ['master_admin', 'admin', 'support', 'finance'],
  usuarios:         ['master_admin', 'admin', 'support'],
  clientes:         ['master_admin', 'admin', 'support'],
  administradores:  ['master_admin'],
  planos:           ['master_admin', 'admin'],
  assinaturas:      ['master_admin', 'admin', 'support', 'finance'],
  pagamentos:       ['master_admin', 'admin', 'finance'],
  gateways:         ['master_admin', 'finance'],
  configuracoes:    ['master_admin'],
  auditoria:        ['master_admin', 'admin'],
  webhooks:         ['master_admin', 'admin'],
  feedbacks:        ['master_admin', 'admin', 'support'],
  sugestoes:        ['master_admin', 'admin'],
  integracoes:      ['master_admin'],
  comunicados:      ['master_admin', 'admin'],
};

export const ALL_MODULES = Object.keys(roleDefaultPermissions);

export const useAdminRole = () => {
  const { role, roleLoading, modulePermissions } = useAuth();

  /**
   * Verifica se o usuário pode acessar um módulo.
   * Lógica:
   *  1. Verifica se o papel tem acesso padrão ao módulo.
   *  2. Se module_permissions está definido E tem uma chave para esse módulo,
   *     esse valor (boolean) sobrescreve o padrão do papel.
   *  Exceção: master_admin sempre tem acesso (segurança).
   */
  const can = (module: string): boolean => {
    if (!role) return false;
    // master_admin sempre tem acesso total (não pode ser bloqueado por module_permissions)
    if (role === 'master_admin') return true;

    const roleDefault = (roleDefaultPermissions[module] ?? []).includes(role);

    // Se há permissões específicas por módulo configuradas, aplicar override
    if (modulePermissions !== null && typeof modulePermissions[module] === 'boolean') {
      return modulePermissions[module];
    }

    return roleDefault;
  };

  return {
    role: role as UserRole | null,
    loading: roleLoading,
    isMasterAdmin:  role === 'master_admin',
    isAdmin:        role === 'admin',
    isSupport:      role === 'support',
    isFinance:      role === 'finance',
    isAnyAdmin:     !!role && role !== 'customer',
    can,
    roleDefaultPermissions,
  };
};
